import { Component, Input, inject, HostListener, ElementRef, computed, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ButtonModule } from 'primeng/button';
import { AvatarModule } from 'primeng/avatar';
import { AuthService, StateService } from '@cosmos/angular/utils';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { RouterModule } from '@angular/router';
import { environment } from 'environments/environment';

@Component({
  selector: 'cosmos-user-dropdown',
  imports: [
    ButtonModule,
    AvatarModule,
    RouterModule,
  ],
  templateUrl: './user-dropdown.component.html',
})
export class UserDropdownComponent {

  private readonly el = inject(ElementRef);

  @Input('app') app: String;
  /** When true, render only the avatar (no name/email block) — the avatar
   *  itself becomes the dropdown trigger. Used by the collapsed sidebar. */
  readonly compact = input<boolean>(false);
  menuOpen = false;
  menuTop = 0;

  protected readonly currentUser = toSignal(this.stateService.currentUser$, {
    initialValue: this.stateService.getCurrentUser(),
  });

  protected readonly items = computed<MenuItem[]>(() => [
    {
      label: this.translateService.instant('account.nav-link'),
      routerLink: 'user',
      icon: 'fa-solid fa-user',
    },
    {
      label: this.translateService.instant('account.settings'),
      routerLink: 'settings',
      icon: 'fa-solid fa-gear',
    },
    {
      label: this.translateService.instant('onboarding.menu-label'),
      icon: 'fa-solid fa-graduation-cap',
      routerLink: 'help',
    },
    this.currentUser()?.viewingAs
      ? {
          label: this.translateService.instant('impersonation.banner.return'),
          command: () => this.exitViewAs(),
          icon: 'fa-solid fa-right-to-bracket',
        }
      : {
          label: this.translateService.instant('button.logout'),
          command: () => this.logOut(),
          icon: 'fa-solid fa-right-from-bracket',
        },
  ]);

  constructor(
    private authService: AuthService,
    private translateService: TranslateService,
    private stateService: StateService,
  ) {}

  toggleMenu(event: MouseEvent): void {
    event.stopPropagation();
    if (!this.menuOpen) {
      const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
      this.menuTop = rect.top;
    }
    this.menuOpen = !this.menuOpen;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.el.nativeElement.contains(event.target)) {
      this.menuOpen = false;
    }
  }

  logOut(): void {
    this.authService.signOut();
  }

  exitViewAs(): void {
    this.authService.exitViewAs().subscribe(() => {
      window.location.href = environment.ADMIN_URL;
    });
  }
}
